import { ImpactedCountryRegion } from '@toggle/toggle';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FlagIcon } from '~/components/flag-icon/FlagIcon';
import { RegionIcon } from '~/components/region-icon/RegionIcon';
import { RatingPill } from '~/widgets/earnings-insight-drawer/earnings-insight-widget/components/rating-pill/RatingPill';

import * as S from './ImpactedCountriesAndRegions.styles';

export interface ImpactedCountriesAndRegionsProps {
  impactedCountries: ImpactedCountryRegion[];
  impactedRegions: ImpactedCountryRegion[];
  title: string;
}

export const ImpactedCountriesAndRegions = ({
  impactedCountries,
  impactedRegions,
  title,
}: ImpactedCountriesAndRegionsProps) => {
  const { t } = useTranslation(['countries', 'insights']);

  const items = useMemo(
    () => [
      ...(impactedCountries?.map(c => ({
        key: `country-${c.code}`,
        name: t(`countries:${c.code}`, {
          defaultValue: c.name,
        }),
        icon: (
          <FlagIcon countryCode={c.code} variant="circular" customSize={20} />
        ),
        rating: c.rating,
        description: c.description,
      }))),
      ...(impactedRegions?.map(r => ({
        key: `region-${r.code}`,
        name: r.name,
        icon: (
          <RegionIcon region={r.name} size={20} fillColor="var(--icon-soft)" />
        ),
        rating: r.rating,
        description: r.description,
      }))),
    ],
    [impactedCountries, impactedRegions],
  );

  if (!items.length) {
    return null;
  }

  return (
    <S.Root>
      <S.Title>{title}</S.Title>
      <S.Table>
        <S.HeaderRow>
          <S.HeaderCell>
            {t('insights:catalyst.regionsTable.countriesAndRegions')}
          </S.HeaderCell>
          <S.HeaderCell>
            {t('insights:catalyst.regionsTable.description')}
          </S.HeaderCell>
          <S.HeaderCell>
            {t('insights:catalyst.regionsTable.impact')}
          </S.HeaderCell>
        </S.HeaderRow>
        {items.map(item => {
          return (
            <S.Row key={item.key}>
              <S.CountryCell>
                <S.TopRow>
                  <S.IconWrapper>{item.icon}</S.IconWrapper>
                  <S.TitleBlock>
                    <S.Name>{item.name}</S.Name>
                  </S.TitleBlock>
                </S.TopRow>
              </S.CountryCell>
              <S.Description>{item.description}</S.Description>
              <S.ImpactCell>
                <RatingPill rating={item.rating} />
              </S.ImpactCell>
            </S.Row>
          );
        })}
      </S.Table>
    </S.Root>
  );
};
